import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../lib/api';

const WS_BASE = 'wss://roulette-api.rushelsilvester.workers.dev';
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000]; // exponential backoff

function readUrlRoom() {
  const p = new URLSearchParams(window.location.search);
  const code = p.get('room');
  const token = p.get('token');
  if (code && token) return { code, dealerToken: token };
  return null;
}

function setUrlRoom(code, dealerToken) {
  const p = new URLSearchParams();
  p.set('room', code);
  p.set('token', dealerToken);
  window.history.replaceState({}, '', `?${p.toString()}`);
}

function clearUrlRoom() {
  window.history.replaceState({}, '', window.location.pathname);
}

export function useRoom() {
  const [room, setRoom] = useState(() => readUrlRoom());
  const [state, setState] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'connecting'|'connected'|'reconnecting'|'disconnected'
  const [notifications, setNotifications] = useState([]); // [{ id, message, ts }]

  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const addNotification = useCallback((message, variant = 'normal') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev.slice(-4), { id, message, variant, ts: Date.now() }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, variant === 'bigwin' ? 6000 : 3500);
  }, []);

  const connectWS = useCallback((roomData) => {
    if (!roomData || !isMountedRef.current) return;

    // Close any existing connection
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnectionStatus('connecting');
    const url = `${WS_BASE}/api/rooms/${roomData.code}/ws?token=${roomData.dealerToken}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!isMountedRef.current) return ws.close();
      reconnectAttemptRef.current = 0;
      setConnectionStatus('connected');
    };

    ws.onmessage = (event) => {
      if (!isMountedRef.current) return;
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'state') setState(msg.data);
        if (msg.type === 'notification') addNotification(msg.message, msg.variant);
      } catch {}
    };

    ws.onclose = () => {
      if (!isMountedRef.current) return;
      wsRef.current = null;
      setConnectionStatus('reconnecting');
      scheduleReconnect(roomData);
    };

    ws.onerror = () => ws.close();
  }, [addNotification]);

  const scheduleReconnect = useCallback((roomData) => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    const delay = RECONNECT_DELAYS[Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1)];
    reconnectAttemptRef.current++;
    reconnectTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) connectWS(roomData);
    }, delay);
  }, [connectWS]);

  // Connect WebSocket when room changes
  useEffect(() => {
    if (!room) {
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      setConnectionStatus('disconnected');
      return;
    }

    reconnectAttemptRef.current = 0;
    connectWS(room);

    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [room, connectWS]);

  // Immediately reconnect when tab becomes visible again (iPad screen wake)
  useEffect(() => {
    if (!room) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const ws = wsRef.current;
        const isConnected = ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING);
        if (!isConnected) {
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          reconnectAttemptRef.current = 0;
          connectWS(room);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [room, connectWS]);

  const createRoom = useCallback(async (opts = {}) => {
    try {
      const data = await api.createRoom(opts);
      if (data.error) return data;
      const roomData = { code: data.code, dealerToken: data.dealerToken };
      setUrlRoom(data.code, data.dealerToken);
      setRoom(roomData);
      return data;
    } catch (err) {
      return { error: err.message || 'Verbindung fehlgeschlagen' };
    }
  }, []);

  const reportSpinStart = useCallback(async (params) => {
    if (!room) return;
    return api.startSpin(room.code, params, room.dealerToken);
  }, [room]);

  const reportSpin = useCallback(async (number) => {
    if (!room) return;
    return api.spin(room.code, number, room.dealerToken);
  }, [room]);

  const startNewRound = useCallback(async () => {
    if (!room) return;
    return api.newRound(room.code, room.dealerToken);
  }, [room]);

  const closeBets = useCallback(async () => {
    if (!room) return;
    return api.closeBets(room.code, room.dealerToken);
  }, [room]);

  const updateSettings = useCallback(async (settings) => {
    if (!room) return;
    return api.settings(room.code, settings, room.dealerToken);
  }, [room]);

  const closeRoom = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    setRoom(null);
    setState(null);
    setConnectionStatus('disconnected');
    clearUrlRoom();
  }, []);

  const checkin = useCallback(async (name, balance) => {
    if (!room) return { error: 'Kein Raum' };
    const data = await api.checkin(room.code, name, balance, room.dealerToken);
    if (data.error) return data;
    const sessionUrl = `https://roulette-player.rushelwedsivani.com/?room=${room.code}&pid=${data.playerId}&ptoken=${data.playerToken}&name=${encodeURIComponent(data.name)}`;
    return { ...data, sessionUrl };
  }, [room]);

  const checkout = useCallback(async (playerId) => {
    if (!room) return { error: 'Kein Raum' };
    return api.checkout(room.code, playerId, room.dealerToken);
  }, [room]);

  const kickPlayer = useCallback(async (playerId) => {
    if (!room) return { error: 'Kein Raum' };
    return api.kickPlayer(room.code, playerId, room.dealerToken);
  }, [room]);

  const playerUrl = room
    ? `https://roulette-player.rushelwedsivani.com/?room=${room.code}`
    : null;

  return {
    room, state, connectionStatus, notifications,
    createRoom, reportSpinStart, reportSpin, startNewRound,
    closeBets, closeRoom, playerUrl, checkin, checkout, kickPlayer, updateSettings,
  };
}
